/**
 * ResultsNewRoot - New Results UI Root Component
 *
 * Entry point for the /results interface.
 * Renders inside existing content area without touching page layout wrappers.
 *
 * Gating: Formerly under RESULTS_NEW_DESIGN; redesign is now the default surface.
 * Rollback: Revert this module if the always-on redesign needs to be rolled back.
 *
 * Step 0-2 of ResultsDemo_BabyStep_Plan implementation
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '../../lib/logging';
import { useAuth } from '../../contexts';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { AnonymousBanner } from '../shared';
import { ResultsSidebar } from './sidebar/ResultsSidebar';
import { ComparisonsList } from './comparisons/ComparisonsList';
import { ComparisonDetail } from './comparisons/ComparisonDetail';
import { ComparisonSortControl } from './comparisons/ComparisonSortControl';
import { ResponsesNewShell } from './responses/ResponsesNewShell';
import { useViewMode } from './state/viewMode';
// REMOVED: import { useModelFilters } from './state/modelFilters';
// REMOVED: import type { ModelDetail } from './state/modelFilters';
import { logComparisonSearch, logResponseSearch } from './state/search';
import { useDensity } from './state/density';
import { useComparisonDetailView } from './state/comparisonDetailView';
import { useFilteredData } from './hooks/useFilteredData';
import {
  comparisonService,
  favoritesService,
  ComparisonDeletionError,
  type ScoringResult,
} from '../../services/comparison';
// S9: Import score-related components and hooks
import { ScoreChip } from './ScoreChip';
import { useComparisonScores } from '../../hooks/useComparisonScores';
import { useMultipleComparisonScores } from '../../hooks/useMultipleComparisonScores';
// Removed: comparisonService (no longer using demo fallback), Comparison/ModelResponse (using types from results.ts)
import type { ConsensusTab } from '../../features/consensus';
import { sortComparisons, type ComparisonSortOption } from '../../utils/comparators';
import analytics from '../../utils/analytics';
// STEP 6: Import useComparisons and useResponses hooks to fetch from DB
// S1: Import useAllResponses to fetch responses across all comparisons for Responses view
import { useComparisons, useResponses, useAllResponses } from '../../hooks/useResults';
// STEP 6D: Import Comparison and Response types from API types (not legacy service)
import type { Comparison, Response } from '../../lib/types/results';
// STEP 7: Import toggleFavorite API for DB persistence
import { toggleFavorite as toggleFavoriteAPI, clearCache } from '../../lib/api/results';
// Import computeWinner for scoring data derivation
import { computeWinner } from '../../utils/winnerComputation';
import { useFeedbackModal } from '../../contexts/FeedbackModalContext';
import { useDevice } from '../../features/consensus/hooks/useDevice';
import { ToastContainer, useToast } from '../ui/Toast';

export interface ResultsNewRootProps {
  comparisonId: string;
}

// REMOVED: buildModelDetails function - model filtering disabled
const getHashParams = (): URLSearchParams => {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }

  return new URLSearchParams(window.location.hash.slice(1));
};

export const ResultsNewRoot: React.FC<ResultsNewRootProps> = ({ comparisonId }) => {
  const navigate = useNavigate();
  const { setContextData: setFeedbackContext } = useFeedbackModal();
  const { isMobile } = useDevice();
  const {
    toasts,
    success: showSuccessToast,
    destructive: showDestructiveToast,
    error: showErrorToast,
    removeToast
  } = useToast();
  const { user } = useAuth();
  const isAnonymous = !user;
  let anonymousFlagEnabled = false;
  try {
    anonymousFlagEnabled = useFeatureFlag('ANONYMOUS');
  } catch {
    anonymousFlagEnabled = false;
  }

  const { viewMode, isComparisonsView, setViewMode } = useViewMode();
  const isSxSEnabled = true; // Always enabled - SxS comparison and scoring
  // REMOVED: Model filtering completely disabled to show all comparisons
  // const { enabledModelIds, hasEnabledModels, enableAllModels, syncModels, modelFilters } = useModelFilters();
  const [cmpQuery, setCmpQueryState] = useState('');
  const [respQuery, setRespQueryState] = useState('');
  const handleComparisonSearchChange = useCallback((query: string) => {
    setCmpQueryState(prev => {
      if (prev === query) {
        return prev;
      }

      logComparisonSearch(query);
      return query;
    });
  }, []);

  const handleResponseSearchChange = useCallback((query: string) => {
    setRespQueryState(prev => {
      if (prev === query) {
        return prev;
      }

      logResponseSearch(query);
      return query;
    });
  }, []);

  const { density, setDensity } = useDensity();
  const { detailView, setDetailView } = useComparisonDetailView();
  const [consensusTab, setConsensusTab] = useState<ConsensusTab>('heatmap');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isResponseDetailOpen, setIsResponseDetailOpen] = useState(false);
  const updateHashParams = useCallback((updater: (params: URLSearchParams) => void) => {
    if (typeof window === 'undefined') {
      return;
    }

    const params = getHashParams();
    updater(params);
    const nextHash = params.toString();
    window.location.hash = nextHash;
  }, []);

  const setComparisonViewHash = useCallback(() => {
    updateHashParams(params => {
      params.set('view', 'comparisons');
      params.delete('id');
      params.delete('from');
    });
  }, [updateHashParams]);

  const setResponseViewHash = useCallback((responseId: string) => {
    updateHashParams(params => {
      params.set('view', 'responses');
      params.set('id', responseId);
    });
  }, [updateHashParams]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const tabLabel = detailView === 'outputs' ? 'Responses' : detailView === 'scoring' ? 'Scores' : 'Consensus';
    const basePath = `${window.location.pathname}${window.location.search}`;
    const tabPath = `${basePath}${basePath.includes('?') ? '&' : '?'}view=${detailView}${window.location.hash}`;
    const pageLocation = `${window.location.origin}${tabPath}`;
    const pageTitle = `Results - ${tabLabel}`;

    analytics.trackPageView(pageTitle, pageLocation, tabPath);
  }, [detailView]);

  // Task 1: SxS Compare Deck state (session-only, max 2 items)
  const [compareDeck, setCompareDeck] = useState<string[]>([]);

  // Task 5: SxS view mode (false = normal CDV, true = side-by-side split view)
  const [isSxSMode, setIsSxSMode] = useState(false);

  // Task 6: SxS split percentage (30-70%, session-only)
  const [sxsSplitPct, setSxsSplitPct] = useState(50);

  // Model Filter state (initialized from URL)
  const initializeFilterStateFromURL = useCallback((): { models: Set<string>; mode: 'any' | 'all' } => {
    const hash = window.location.hash.slice(1); // Remove #
    const params = new URLSearchParams(hash);

    const modelsParam = params.get('models');
    const modeParam = params.get('mode');

    const models = modelsParam ? new Set(modelsParam.split(',').filter(id => id.length > 0)) : new Set();
    const mode = (modeParam === 'all' ? 'all' : 'any') as 'any' | 'all';

    return { models, mode };
  }, []);

  const [selectedModels, setSelectedModels] = useState<Set<string>>(() => initializeFilterStateFromURL().models);
  const [filterMode, setFilterMode] = useState<'any' | 'all'>(() => initializeFilterStateFromURL().mode);

  // Update URL hash with filter state
  const updateFilterInURL = useCallback((models: Set<string>, mode: 'any' | 'all') => {
    const hash = window.location.hash.slice(1);
    const params = new URLSearchParams(hash);

    if (models.size > 0) {
      params.set('models', Array.from(models).join(','));
      params.set('mode', mode);
    } else {
      // Clear filter params when no models selected
      params.delete('models');
      params.delete('mode');
    }

    window.location.hash = params.toString();
  }, []);

  // Handle model filter changes
  const handleFilterChange = useCallback((newSelectedModels: Set<string>, newFilterMode: 'any' | 'all') => {
    console.log('[ResultsNewRoot] handleFilterChange CALLED', {
      newSelectedModels: Array.from(newSelectedModels),
      newFilterMode,
      previousSelectedModels: Array.from(selectedModels),
      previousFilterMode: filterMode
    });

    setSelectedModels(newSelectedModels);
    setFilterMode(newFilterMode);
    updateFilterInURL(newSelectedModels, newFilterMode);

    logger.info('model_filter_changed', undefined, {
      op: 'filter_changed',
      component: 'ResultsNewRoot',
      selectedCount: newSelectedModels.size,
      filterMode: newFilterMode,
      selectedModels: Array.from(newSelectedModels)
    });
  }, [updateFilterInURL, selectedModels, filterMode]);

  // Listen for hash changes (back/forward navigation)
  useEffect(() => {
    const handleHashChange = () => {
      const { models, mode } = initializeFilterStateFromURL();
      setSelectedModels(models);
      setFilterMode(mode);

      logger.info('model_filter_restored', undefined, {
        op: 'filter_restored',
        component: 'ResultsNewRoot',
        selectedCount: models.size,
        filterMode: mode,
        source: 'hash_change',
        selectedModels: Array.from(models)
      });
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [initializeFilterStateFromURL]);

  // Comparisons sort state with URL sync
  const [sortOption, setSortOption] = useState<ComparisonSortOption>('recent');
  const allowedSortOptions = useMemo<ComparisonSortOption[]>(() => {
    const options: ComparisonSortOption[] = ['recent', 'oldest', 'favorites', 'title', 'score'];
    return isAnonymous ? options.filter(option => option !== 'favorites' && option !== 'score') : options;
  }, [isAnonymous]);

  // Read sort from URL hash on mount
  useEffect(() => {
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : '');
    const sortParam = params.get('sort') as ComparisonSortOption | null;
    if (sortParam && allowedSortOptions.includes(sortParam)) {
      setSortOption(sortParam);
      return;
    }

    setSortOption(prev => (allowedSortOptions.includes(prev) ? prev : allowedSortOptions[0]));
  }, [allowedSortOptions]);

  // Update URL hash when sort changes
  const handleSortChange = (sort: ComparisonSortOption) => {
    if (!allowedSortOptions.includes(sort)) {
      return;
    }

    setSortOption(sort);

    const hash = window.location.hash.substring(1);
    const [path, queryString] = hash.split('?');
    const params = new URLSearchParams(queryString || '');
    params.set('sort', sort);
    window.location.hash = `${path}?${params.toString()}`;
  };

  // Generate correlationId for this session
  const [correlationId] = useState(() => `results_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  const collapseSidebarForResponseDetail = useCallback(
    (source: 'response_detail_open' | 'response_detail_visit') => {
      setSidebarCollapsed(prev => {
        if (prev) {
          return prev;
        }

        logger.info('results_sidebar_auto_collapse', correlationId, {
          op: 'auto_collapse_sidebar',
          component: 'ResultsNewRoot',
          correlationId,
          source,
        });

        return true;
      });
    },
    [correlationId]
  );

  useEffect(() => {
    if (!isResponseDetailOpen) {
      return;
    }

    collapseSidebarForResponseDetail('response_detail_open');
  }, [isResponseDetailOpen, collapseSidebarForResponseDetail]);

  const handleResponseDetailOpenChange = useCallback((isOpen: boolean) => {
    setIsResponseDetailOpen(isOpen);
  }, []);

  const handleResponseDetailVisit = useCallback(() => {
    collapseSidebarForResponseDetail('response_detail_visit');
  }, [collapseSidebarForResponseDetail]);

  // STEP 6: Fetch comparisons from DB using hook (replaces hardcoded comparisonService call)
  // If comparisonId is provided, fetch only that specific comparison
  // Otherwise, fetch all user comparisons with pagination
  const {
    comparisons: dbComparisons,
    loading: comparisonsLoading,
    loadingMore: comparisonsLoadingMore,
    error: comparisonsError,
    hasMore: hasMoreComparisons,
    loadMore: loadMoreComparisons,
    refetch: refetchComparisons
  } = useComparisons(
    comparisonId && comparisonId !== 'demo'
      ? { comparisonId, limit: 1 }
      : { limit: 20, sort: '-createdAt' }
  );

  // State for responses (comparisons now come from useComparisons hook)
  const [comparisons, setComparisons] = useState<Comparison[]>([]);
  const [responses, setResponses] = useState<Response[]>([]);
  const [scoringResults, setScoringResults] = useState<ScoringResult[]>([]);
  const [winnerAiId, setWinnerAiId] = useState<string | null>(null);
  const [winnerScore, setWinnerScore] = useState<number | null>(null);
  const [deletingComparisonId, setDeletingComparisonId] = useState<string | null>(null);
  const [deleteFeedback, setDeleteFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const lastDeleteToastRef = useRef<string | null>(null);
  const dismissDeleteFeedback = useCallback(() => setDeleteFeedback(null), []);
  const [resolvedComparisonId, setResolvedComparisonId] = useState<string | null>(null);
  const lastComparisonParamRef = useRef<string | null>(comparisonId || null);
  const pendingSelectionRef = useRef<string | null>(null);

  // Clear deletion feedback when navigating between comparisons
  useEffect(() => {
    setDeleteFeedback(null);
  }, [comparisonId]);

  // Optimistic comparison state to prevent flash during navigation
  const [optimisticComparison, setOptimisticComparison] = useState<Comparison | null>(null);
  const [forceComparisonsList, setForceComparisonsList] = useState(false);

  // Track and resolve comparison IDs so data fetches always use UUIDs even when the URL shows a slug
  useEffect(() => {
    if (!comparisonId || comparisonId === 'demo') {
      // FIX: Unconditionally clear ALL selection state when navigating to list view
      // This ensures URL and state stay synchronized when back button is tapped on mobile
      if (resolvedComparisonId !== null) {
        setResolvedComparisonId(null);
      }
      if (pendingSelectionRef.current !== null) {
        pendingSelectionRef.current = null;
      }
      if (optimisticComparison !== null) {
        setOptimisticComparison(null);
      }
      lastComparisonParamRef.current = comparisonId || null;
      return;
    }

    if (comparisonId !== lastComparisonParamRef.current) {
      lastComparisonParamRef.current = comparisonId;

      // If this navigation did not originate from an in-app selection, clear any stale resolution
      if (!pendingSelectionRef.current) {
        setResolvedComparisonId(null);
      }
    }

    // When comparisons are loaded, map the URL param (slug or UUID) to the canonical UUID
    const match = comparisons.find(c => c.id === comparisonId || c.slug === comparisonId);
    if (match && resolvedComparisonId !== match.id) {
      setResolvedComparisonId(match.id);
      logger.info('comparison_id_resolved', correlationId, {
        op: 'comparison_id_resolved',
        component: 'ResultsNewRoot',
        comparisonParam: comparisonId,
        resolvedComparisonId: match.id,
        hasSlug: Boolean(match.slug)
      });
    }
  }, [comparisonId, comparisons, correlationId, resolvedComparisonId, optimisticComparison]);

  const comparisonKey = forceComparisonsList
    ? null
    : resolvedComparisonId || pendingSelectionRef.current || comparisonId;

  const selectedComparison = useMemo(() => {
    if (!comparisonKey || comparisonKey === 'demo') {
      return null;
    }

    return (
      comparisons.find(c => c.id === comparisonKey || c.slug === comparisonKey) || null
    );
  }, [comparisonKey, comparisons]);
  const [initialResponseId, setInitialResponseId] = useState<string | null>(null);
  const lastDetailLoggedRef = useRef<string | null>(null);

  // Keep hash in sync with the current view mode to avoid stale response URLs
  useEffect(() => {
    if (viewMode === 'comparisons') {
      setComparisonViewHash();
      return;
    }

    if (viewMode === 'responses' && initialResponseId) {
      setResponseViewHash(initialResponseId);
    }
  }, [viewMode, initialResponseId, setComparisonViewHash, setResponseViewHash]);

  // Initialize initialResponseId from URL hash immediately after mount (for direct RDV navigation)
  // Using useLayoutEffect to run synchronously before paint, preventing blank list flash
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const params = getHashParams();
    const hash = window.location.hash.slice(1);
    const view = params.get('view');
    const responseId = params.get('id');

    console.log('[ResultsNewRoot] useLayoutEffect - Initializing initialResponseId from hash:', {
      hash,
      view,
      responseId,
      fullURL: window.location.href
    });

    if (view === 'responses' && responseId) {
      console.log('[ResultsNewRoot] Setting initialResponseId from hash:', responseId);
      setInitialResponseId(responseId);
    }
  }, []); // Empty deps - only run once on mount

  // Watch for hash changes to handle navigation from consensus to response detail view
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      const params = getHashParams();
      const view = params.get('view');
      const responseId = params.get('id');
      const fromConsensus = params.get('from') === 'consensus';

      console.log('[ResultsNewRoot] Hash changed:', {
        hash,
        view,
        responseId,
        fromConsensus,
        currentViewMode: viewMode
      });

      // If navigating to response view from consensus
      if (view === 'responses' && responseId && selectedComparison) {
        console.log('[ResultsNewRoot] Setting viewMode to responses from hash change');
        setViewMode('responses');
        setInitialResponseId(responseId);

        logger.info('response_view_from_consensus', correlationId, {
          op: 'response_view_from_consensus',
          component: 'ResultsNewRoot',
          response_id: responseId,
          comparison_id: selectedComparison.id,
          from_consensus: fromConsensus,
          correlationId
        });
      }
    };

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    // Also run on mount to handle initial hash
    handleHashChange();

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [selectedComparison, correlationId, viewMode, setViewMode]);

  // Use optimistic comparison as fallback to prevent flash during navigation
  const displayComparison = selectedComparison || optimisticComparison;

  useEffect(() => {
    if (
      optimisticComparison &&
      selectedComparison &&
      optimisticComparison.id === selectedComparison.id
    ) {
      setOptimisticComparison(null);
      pendingSelectionRef.current = null;
    }
  }, [optimisticComparison, selectedComparison]);

  useEffect(() => {
    if (!comparisonId || comparisonId === 'demo') {
      if (!pendingSelectionRef.current && optimisticComparison) {
        setOptimisticComparison(null);
      }
      return;
    }
  }, [comparisonId, optimisticComparison]);

  // STEP 6B: Fetch responses for selected comparison from DB (must be after selectedComparison state declaration)
  // Use comparisonId directly to ensure responses fetch immediately on initial redirect (not waiting for displayComparison to be available)
  const {
    responses: dbResponses,
    loading: responsesLoading,
    error: responsesError,
    refreshing: responsesRefreshing,
    refetch: refetchResponses
  } = useResponses((comparisonKey && comparisonKey !== 'demo') ? comparisonKey : null);

  // S1: Fetch ALL responses for Responses view AND Comparisons view (for Model Filter)
  const comparisonIds = useMemo(() => comparisons.map(c => c.id), [comparisons]);
  const {
    responses: allDbResponses,
    loading: allResponsesLoading,
    error: allResponsesError,
  } = useAllResponses(comparisonIds); // Fetch for both views (needed for Model Filter in Comparisons view)

  // S9: Fetch composite scores for selected comparison
  const { scores: compositeScores, featureEnabled: scoresFeatureEnabled, refetch: refetchScores, refetchAndWait: refetchScoresAndWait } = useComparisonScores(displayComparison?.id);

  // Fetch composite scores for all comparisons in the list (for gold border display)
  const allComparisonIds = useMemo(() => comparisons.map(c => c.id), [comparisons]);
  const {
    scoresMap: allCompositeScoresMap,
    refetchAllScores: refetchAllComparisonScores,
  } = useMultipleComparisonScores(allComparisonIds);

  // Debug: Log hook result
  useEffect(() => {
    console.log('[ResultsNewRoot] useComparisonScores result:', {
      selectedComparisonId: selectedComparison?.id,
      scoresEnabled: scoresFeatureEnabled,
      hasScores: !!compositeScores,
      scoresCount: compositeScores?.length || 0,
      scores: compositeScores
    });
  }, [selectedComparison?.id, compositeScores, scoresFeatureEnabled]);

  const [favorites, setFavorites] = useState<Set<string>>(() => {
    // Load favorites from localStorage on mount
    const favoriteIds = favoritesService.getFavorites();
    return new Set(favoriteIds);
  });

  // Router owns selection state; log popstate for diagnostics only
  useEffect(() => {
    const handlePopState = () => {
      logger.debug?.('comparison_popstate_event', undefined, {
        op: 'comparison_popstate_event',
        component: 'ResultsNewRoot',
        pathname: window.location.pathname,
        comparisonIdParam: comparisonId,
        correlationId
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [comparisonId, correlationId]);

  // STEP 6: Use DB comparisons when available, fall back to demo service for responses/scoring
  useEffect(() => {
    const startTime = performance.now();

    logger.info('results_data_fetch_start', correlationId, {
      op: 'results_data_fetch_start',
      component: 'ResultsNewRoot',
      comparisonId,
      correlationId,
      source: 'db' // STEP 6: Now fetching from DB
    });

    // STEP 6: If DB comparisons loaded, use them
    if (!comparisonsLoading && !comparisonsError && dbComparisons.length > 0) {
      setComparisons(dbComparisons);

      logger.info('results_db_fetch_success', correlationId, {
        op: 'results_db_fetch_success',
        component: 'ResultsNewRoot',
        comparisonId,
        correlationId,
        comparisonsCount: dbComparisons.length,
        duration_ms: performance.now() - startTime,
        success: true
      });

      // STEP 6B: Responses now fetched via useResponses hook when selectedComparison changes
      // No longer need to fetch from comparisonService
    }

    // REMOVED: Demo service fallback - only use API data
    if (comparisonsError) {
      logger.error('comparisons_api_error', correlationId, {
        op: 'comparisons_api_error',
        component: 'ResultsNewRoot',
        comparisonId,
        correlationId,
        error: comparisonsError,
        duration_ms: performance.now() - startTime,
        success: false
      });
    }
  }, [comparisonId, correlationId, comparisonsLoading, comparisonsError, dbComparisons]);

  // Router is the single source of truth for selection; log sync events
  const lastSelectionRef = useRef<{ resolvedId: string | null; param: string | null }>({
    resolvedId: null,
    param: null
  });
  useEffect(() => {
    if (comparisons.length === 0) {
      return;
    }

    const resolvedId = selectedComparison?.id ?? null;
    const previous = lastSelectionRef.current;
    const currentParam = comparisonId ?? null;
    if (previous.resolvedId === resolvedId && previous.param === currentParam) {
      return;
    }

    lastSelectionRef.current = { resolvedId, param: currentParam };

    logger.info('comparison_selection_sync', correlationId, {
      op: 'comparison_selection_sync',
      component: 'ResultsNewRoot',
      comparisonIdParam: comparisonId,
      resolvedComparisonId: resolvedId,
      comparisonsCount: comparisons.length,
      selectionSource: 'router',
      optimisticFallbackActive: Boolean(!selectedComparison && optimisticComparison),
      success: Boolean(resolvedId || !comparisonId || comparisonId === 'demo')
    });

    if (comparisonId && comparisonId !== 'demo' && !selectedComparison) {
      const diagnosticInfo = {
        comparisonId,
        comparisonKey,
        inputFormat: comparisonId.includes('-') ? 'slug' : 'uuid',
        availableIds: comparisons.map(c => c.id),
        availableSlugs: comparisons.map(c => c.slug).filter(Boolean),
        comparisonsCount: comparisons.length,
        optimisticComparisonId: optimisticComparison?.id,
        currentUrl: window.location.href
      };

      console.warn('[ResultsNewRoot] Router selection missing comparison:', diagnosticInfo);

      logger.warn('comparison_not_found_in_list', diagnosticInfo, {
        op: 'comparison_selection',
        component: 'ResultsNewRoot',
        correlationId,
        success: false
      });
    }
  }, [
    comparisonId,
    comparisons,
    correlationId,
    comparisonKey,
    optimisticComparison,
    selectedComparison
  ]);

  // Initialize response view from URL hash (RDV URLs support)
  // This runs after useLayoutEffect has set initialResponseId on mount
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    const params = getHashParams();
    const view = params.get('view');
    const responseId = params.get('id');

    console.log('[ResultsNewRoot] Hash Init Effect:', {
      hash,
      view,
      responseId,
      currentViewMode: viewMode,
      selectedComparisonId: selectedComparison?.id,
      currentInitialResponseId: initialResponseId
    });

    // Skip if already initialized by useLayoutEffect (prevents double initialization)
    if (view === 'responses' && responseId && initialResponseId === responseId) {
      console.log('[ResultsNewRoot] Skipping - already initialized from useLayoutEffect');
      return;
    }

    // If hash specifies responses view with responseId, initialize it
    // This handles dynamic hash changes (e.g., clicking between responses)
    if (view === 'responses' && responseId && selectedComparison) {
      console.log('[ResultsNewRoot] Initializing response view from hash (dynamic change)');
      setViewMode('responses');
      setInitialResponseId(responseId);

      logger.info('response_initialized_from_hash', correlationId, {
        op: 'response_initialized_from_hash',
        component: 'ResultsNewRoot',
        response_id: responseId,
        comparison_id: selectedComparison.id,
        correlationId
      });
    }
  }, [selectedComparison?.id, correlationId, viewMode, initialResponseId]);

  // STEP 6C + S1: Update responses based on view mode
  // - Comparisons view: use dbResponses for selected comparison
  // - Responses view: use allDbResponses across all comparisons
  useEffect(() => {
    console.log('[ResultsNewRoot] Responses Effect:', {
      selectedComparisonId: selectedComparison?.id,
      responsesLoading,
      responsesError,
      dbResponsesCount: dbResponses.length,
      allDbResponsesCount: allDbResponses.length,
      isComparisonsView
    });

    // S1: In Responses view, use allDbResponses
    if (!isComparisonsView) {
      setResponses(allDbResponses);

      if (allResponsesLoading && allDbResponses.length === 0) {
        logger.debug('all_responses_db_fetch_loading', undefined, {
          correlationId,
          op: 'all_responses_db_fetch',
          component: 'ResultsNewRoot',
          viewMode: 'responses'
        });
      } else if (allDbResponses.length > 0) {
        logger.info('all_responses_db_fetch_success', {
          responsesCount: allDbResponses.length,
          comparisonCount: comparisonIds.length
        }, {
          correlationId,
          op: 'all_responses_db_fetch',
          component: 'ResultsNewRoot',
          viewMode: 'responses',
          success: true
        });
      }
      return;
    }

    // Comparisons view:
    // - If no comparison selected (list view), use allDbResponses for Model Filter
    // - If comparison selected (detail view), use dbResponses for that comparison
    if (!selectedComparison) {
      setResponses(allDbResponses);
      return;
    }

    if (responsesLoading && dbResponses.length === 0) {
      console.log('[ResultsNewRoot] Responses still loading...');
      logger.debug('responses_db_fetch_loading', undefined, {
        correlationId,
        op: 'responses_db_fetch',
        component: 'ResultsNewRoot',
        comparisonId: selectedComparison.id
      });
      return;
    }

    if (responsesError && dbResponses.length === 0) {
      console.error('[ResultsNewRoot] Responses error:', responsesError);
      setResponses([]);
      logger.error('responses_db_fetch_error', { error: responsesError }, {
        correlationId,
        op: 'responses_db_fetch',
        component: 'ResultsNewRoot',
        comparisonId: selectedComparison.id,
        success: false,
        err_message: responsesError
      });
      return;
    }

    console.log('[ResultsNewRoot] Set responses:', dbResponses.length);
    setResponses(dbResponses);

    if (responsesError && dbResponses.length > 0) {
      logger.warn('responses_db_fetch_partial', { error: responsesError }, {
        correlationId,
        op: 'responses_db_fetch',
        component: 'ResultsNewRoot',
        comparisonId: selectedComparison.id,
        success: true,
        fromCache: true
      });
      return;
    }

    logger.info('responses_db_fetch_success', {
      responsesCount: dbResponses.length,
      comparisonId: selectedComparison.id
    }, {
      correlationId,
      op: 'responses_db_fetch',
      component: 'ResultsNewRoot',
      success: true
    });
  }, [
    isComparisonsView,
    allDbResponses,
    allResponsesLoading,
    dbResponses,
    responsesLoading,
    responsesError,
    selectedComparison,
    correlationId,
    comparisonIds.length
  ]);

  // Clear initialResponseId when switching back to Comparisons view
  useEffect(() => {
    if (isComparisonsView && initialResponseId) {
      setInitialResponseId(null);
    }
  }, [isComparisonsView, initialResponseId]);

  // Task 1: Clear compare deck when exiting CDV (no selected comparison)
  useEffect(() => {
    if (!selectedComparison && compareDeck.length > 0) {
      setCompareDeck([]);
      setIsSxSMode(false); // Task 5: Also exit SxS mode

      logger.info('sxs_deck_cleared_on_exit', correlationId, {
        op: 'sxs_deck_cleared',
        component: 'ResultsNewRoot',
        correlationId,
        previousDeckSize: compareDeck.length,
        reason: 'exited_cdv',
        success: true
      });
    }
  }, [selectedComparison, compareDeck.length, correlationId]);

  // Task 5: Auto-exit SxS mode if deck size changes (removed item → deck < 2)
  useEffect(() => {
    if (isSxSMode && compareDeck.length < 2) {
      setIsSxSMode(false);

      logger.info('sxs_mode_auto_exit', correlationId, {
        op: 'sxs_mode_toggle',
        component: 'ResultsNewRoot',
        correlationId,
        newMode: 'normal',
        reason: 'deck_size_below_2',
        deckSize: compareDeck.length,
        success: true
      });
    }
  }, [isSxSMode, compareDeck.length, correlationId]);

  // NEW: Derive scoringResults from response-level scores
  useEffect(() => {
    if (!selectedComparison || responses.length === 0) {
      setScoringResults([]);
      setWinnerAiId(null);
      setWinnerScore(null);
      return;
    }

    // Build ScoringResult[] from each response's scores field
    const derivedScoring: ScoringResult[] = [];

    responses.forEach(response => {
      if (!response.scores || typeof response.scores !== 'object') {
        return;
      }

      // Convert scores object to ScoringResult entries
      Object.entries(response.scores).forEach(([criterionName, score]) => {
        derivedScoring.push({
          id: `${response.id}-${criterionName}`, // synthetic ID
          responseId: response.id,
          criterionName,
          score: typeof score === 'number' ? score : 0,
          reasoning: '', // Not available from Response type
          strengths: undefined,
          weaknesses: undefined,
          suggestions: undefined,
          weightedScore: typeof score === 'number' ? score : 0,
          overallAssessment: undefined,
          winningFactors: undefined,
          improvementAreas: undefined,
          judgeModel: 'derived',
          judgeProvider: 'api',
          judgingCost: 0,
          createdAt: response.createdAt,
        });
      });
    });

    setScoringResults(derivedScoring);

    // Compute winner using updated computeWinner with composite scores
    const { winnerId, winnerScore } = computeWinner(responses, derivedScoring, compositeScores || undefined);
    setWinnerAiId(winnerId);
    setWinnerScore(winnerScore);

    logger.info('scoring_data_derived', {
      comparisonId: selectedComparison.id,
      scoringCount: derivedScoring.length,
      winnerId,
      winnerScore,
    }, {
      correlationId,
      op: 'scoring_data_derived',
      component: 'ResultsNewRoot',
      success: true,
    });
  }, [responses, selectedComparison, correlationId]);

  useEffect(() => {
    if (!selectedComparison) {
      return;
    }

    if (lastDetailLoggedRef.current === selectedComparison.id) {
      return;
    }

    lastDetailLoggedRef.current = selectedComparison.id;

    logger.info('comparison_detail_viewed', {
      comparisonId: selectedComparison.id,
      responseCount: dbResponses.length,
    }, {
      correlationId,
      op: 'comparison_detail_viewed',
      component: 'ResultsNewRoot',
      success: true
    });
  }, [selectedComparison, dbResponses.length, correlationId]);

  const handleComparisonClick = (comp: Comparison) => {
    setForceComparisonsList(false);
    // Set optimistic state first to prevent flash during navigation
    setOptimisticComparison(comp);
    pendingSelectionRef.current = comp.id;
    setResolvedComparisonId(comp.id);

    // Debug logging for slug investigation
    if (!comp.slug) {
      logger.warn('comparison_missing_slug', correlationId, {
        op: 'comparison_missing_slug',
        component: 'ResultsNewRoot',
        comparisonId: comp.id,
        title: comp.title,
        createdAt: comp.createdAt,
        message: 'Comparison object missing slug field - falling back to UUID'
      });
    }

    // Update URL to use slug (or fallback to UUID if slug unavailable)
    const idOrSlug = comp.slug || comp.id;
    setComparisonViewHash();
    navigate(`/results/${idOrSlug}`);
    setViewMode('comparisons');

    logger.info('comparison_card_clicked', correlationId, {
      op: 'comparison_card_clicked',
      component: 'ResultsNewRoot',
      comparisonId: comp.id,
      slug: comp.slug,
      navigatedTo: `/results/${idOrSlug}`,
      correlationId,
      viewMode
    });
  };

  const handleBackToList = () => {
    setForceComparisonsList(true);
    setOptimisticComparison(null);
    pendingSelectionRef.current = null;
    setResolvedComparisonId(null);
    setInitialResponseId(null);
    setComparisonViewHash();
    setViewMode('comparisons');

    // Navigate back to CPV (comparison panel view) - clear slug from URL
    navigate('/results');

    logger.info('comparison_detail_back', correlationId, {
      op: 'comparison_detail_back',
      component: 'ResultsNewRoot',
      navigatedTo: '/results',
      correlationId
    });
  };

  const handleNavigateToParent = () => {
    if (!selectedComparison) return;

    setForceComparisonsList(false);
    // Reset response detail state when navigating back to comparison
    setIsResponseDetailOpen(false);
    setInitialResponseId(null);
    setViewMode('comparisons');
    setComparisonViewHash();

    const hasParent = !!selectedComparison.parentId;
    if (hasParent) {
      // Navigate to parent comparison
      const parentComp = filteredData.comparisons.find(c => c.id === selectedComparison.parentId);
        if (parentComp) {
          setOptimisticComparison(parentComp);
          pendingSelectionRef.current = parentComp.id;
          setResolvedComparisonId(parentComp.id);
          const slugOrId = parentComp.slug || parentComp.id;
          navigate(`/results/${slugOrId}`);
        logger.info('navigate_to_parent', correlationId, {
          op: 'navigate_to_parent',
          component: 'ResultsNewRoot',
          fromComparisonId: selectedComparison.id,
          toComparisonId: parentComp.id,
          slug: parentComp.slug,
          correlationId
        });
        } else {
          // Parent not found, fall back to list
          setOptimisticComparison(null);
          pendingSelectionRef.current = null;
          setResolvedComparisonId(null);
          navigate('/results');
        logger.info('navigate_to_parent_fallback', correlationId, {
          op: 'navigate_to_parent_fallback',
          component: 'ResultsNewRoot',
          fromComparisonId: selectedComparison.id,
          parentId: selectedComparison.parentId,
          reason: 'parent_not_found',
          correlationId
        });
      }
    } else {
      // No parent, navigate to all comparisons
      setOptimisticComparison(null);
      pendingSelectionRef.current = null;
      setResolvedComparisonId(null);
      navigate('/results');
      logger.info('navigate_to_parent_fallback', correlationId, {
        op: 'navigate_to_parent_fallback',
        component: 'ResultsNewRoot',
        fromComparisonId: selectedComparison.id,
        reason: 'no_parent',
        correlationId
      });
    }
  };

  const handleNavigateToAll = () => {
    setForceComparisonsList(true);
    setOptimisticComparison(null);
    pendingSelectionRef.current = null;
    setResolvedComparisonId(null);
    setInitialResponseId(null);
    setComparisonViewHash();
    setViewMode('comparisons');

    // Navigate to CPV (all comparisons view)
    navigate('/results');

    logger.info('navigate_to_all', correlationId, {
      op: 'navigate_to_all',
      component: 'ResultsNewRoot',
      fromComparisonId: selectedComparison?.id,
      navigatedTo: '/results',
      correlationId
    });
  };

  const handleFavoriteToggle = async (compId: string) => {
    const wasFavorited = favorites.has(compId);
    const newIsFavorite = !wasFavorited;

    // STEP 7: Optimistic update - update state immediately
    const newFavorites = new Set(favorites);
    if (wasFavorited) {
      newFavorites.delete(compId);
    } else {
      newFavorites.add(compId);
    }
    setFavorites(newFavorites);

    // STEP 7: Persist to localStorage as fallback (synchronous)
    favoritesService.toggleFavorite(compId, wasFavorited);

    logger.info(wasFavorited ? 'favorite_removed' : 'favorite_added', correlationId, {
      op: wasFavorited ? 'favorite_removed' : 'favorite_added',
      component: 'ResultsNewRoot',
      comparisonId: compId,
      correlationId
    });

    // STEP 7: Persist to DB via API with rollback on error
    try {
      await toggleFavoriteAPI(compId, newIsFavorite);

      logger.info('favorite_persisted_to_db', correlationId, {
        op: 'favorite_persisted_to_db',
        component: 'ResultsNewRoot',
        comparisonId: compId,
        isFavorite: newIsFavorite,
        correlationId,
        success: true
      });
    } catch (error) {
      // Rollback optimistic update on error
      setFavorites(favorites); // Revert to original state
      favoritesService.toggleFavorite(compId, !wasFavorited); // Revert localStorage

      logger.error('favorite_persist_error', correlationId, {
        op: 'favorite_persist_error',
        component: 'ResultsNewRoot',
        comparisonId: compId,
        correlationId,
        error: error instanceof Error ? error.message : 'Unknown error',
        success: false
      });
    }
  };

  const handleDeleteComparison = useCallback(
    async (compId: string) => {
      setDeleteFeedback(null);
      setDeletingComparisonId(compId);

      logger.info('comparison_delete_start', correlationId, {
        op: 'comparison_delete_start',
        component: 'ResultsNewRoot',
        comparisonId: compId,
        correlationId,
      });

      try {
        await comparisonService.deleteComparison(compId);

        setComparisons(prev => prev.filter(comparison => comparison.id !== compId));
        setResponses(prev => {
          const next = prev.filter(response => response.comparisonId !== compId);
          setScoringResults(current =>
            current.filter(result => next.some(response => response.id === result.responseId))
          );
          return next;
        });

        setFavorites(prev => {
          if (!prev.has(compId)) {
            return prev;
          }

          const next = new Set(prev);
          next.delete(compId);
          favoritesService.toggleFavorite(compId, true);
          return next;
        });

        const wasSelected = selectedComparison?.id === compId;
        if (wasSelected) {
          setWinnerAiId(null);
          setWinnerScore(null);
          setOptimisticComparison(null);
          pendingSelectionRef.current = null;
          navigate('/results');
        }

        clearCache();
        refetchComparisons();
        refetchResponses();

        setDeleteFeedback({ type: 'success', message: 'Comparison deleted.' });

        logger.info('comparison_delete_success', correlationId, {
          op: 'comparison_delete_success',
          component: 'ResultsNewRoot',
          comparisonId: compId,
          correlationId,
          success: true,
        });
      } catch (error) {
        let message = 'Failed to delete comparison';
        if (error instanceof ComparisonDeletionError || error instanceof Error) {
          message = error.message;
        }

        setDeleteFeedback({ type: 'error', message });

        logger.error('comparison_delete_error', correlationId, {
          op: 'comparison_delete_error',
          component: 'ResultsNewRoot',
          comparisonId: compId,
          correlationId,
          success: false,
          err_message: message,
        });
      } finally {
        setDeletingComparisonId(null);
      }
    },
    [correlationId, navigate, refetchComparisons, refetchResponses, selectedComparison]
  );

  const handleComparisonUpdated = useCallback(
    (updatedComparisonId: string) => {
      const startTime = performance.now();
      logger.info('comparison_scores_refetch_start', correlationId, {
        op: 'refetch-comparison-scores',
        component: 'ResultsNewRoot',
        comparisonId: updatedComparisonId,
        correlationId,
      });

      try {
        refetchComparisons();
        refetchAllComparisonScores();
        logger.info('comparison_scores_refetch_complete', correlationId, {
          op: 'refetch-comparison-scores',
          component: 'ResultsNewRoot',
          comparisonId: updatedComparisonId,
          correlationId,
          duration_ms: Math.round(performance.now() - startTime),
          success: true,
        });
      } catch (error) {
        logger.error('comparison_scores_refetch_error', correlationId, {
          op: 'refetch-comparison-scores',
          component: 'ResultsNewRoot',
          comparisonId: updatedComparisonId,
          correlationId,
          success: false,
          err_message: error instanceof Error ? error.message : 'unknown-error',
        });
      }
    },
    [correlationId, refetchAllComparisonScores, refetchComparisons]
  );

  const handleOpenParent = (parentComparisonId: string) => {
    setForceComparisonsList(false);
    // Reset response detail state when navigating back to comparison
    setIsResponseDetailOpen(false);
    setInitialResponseId(null);

    // Switch to Comparisons view
    setViewMode('comparisons');
    setComparisonViewHash();

    // Show sidebar but keep it collapsed when navigating to parent comparison
    setSidebarCollapsed(true);

    // Find and open the parent comparison detail view
    const parentComp = filteredData.comparisons.find(c => c.id === parentComparisonId);
    if (parentComp) {
      setOptimisticComparison(parentComp);
      pendingSelectionRef.current = parentComp.id;
      setResolvedComparisonId(parentComp.id);
      navigate(`/results/${parentComp.slug || parentComp.id}`);
    }

    logger.info('open_parent_clicked', correlationId, {
      op: 'open_parent_clicked',
      component: 'ResultsNewRoot',
      response_id: parentComparisonId,
      comparison_id: parentComparisonId,
      correlationId,
      found: !!parentComp
    });
  };

  const handleOpenResponseDetail = (responseId: string) => {
    setForceComparisonsList(false);
    // Switch to Responses view
    setViewMode('responses');

    // Set the initial response ID so ResponsesNewShell opens it
    setInitialResponseId(responseId);

    // Update URL hash to include responseId for direct linking (RDV URLs)
    setResponseViewHash(responseId);

    logger.info('open_response_from_comparison', correlationId, {
      op: 'open_response_from_comparison',
      component: 'ResultsNewRoot',
      response_id: responseId,
      correlationId,
      urlHash: typeof window !== 'undefined' ? window.location.hash.slice(1) : undefined
    });
  };

  const handleNavigateToAllComparisons = () => {
    setForceComparisonsList(true);
    // Reset response detail state when navigating back to comparisons list
    setIsResponseDetailOpen(false);
    setInitialResponseId(null);
    setComparisonViewHash();
    setResolvedComparisonId(null);

    // Expand sidebar when returning to comparisons list
    setSidebarCollapsed(false);

    // Switch to Comparisons view and clear selected comparison
    setViewMode('comparisons');
    setOptimisticComparison(null);
    pendingSelectionRef.current = null;

    // Navigate to CPV (clear slug from URL)
    navigate('/results');

    logger.info('navigate_to_all_comparisons', correlationId, {
      op: 'navigate_to_all_comparisons',
      component: 'ResultsNewRoot',
      navigatedTo: '/results',
      correlationId
    });
  };

  const handleClearFilters = () => {
    const startTime = performance.now();

    if (cmpQuery) {
      handleComparisonSearchChange('');
    }
    if (respQuery) {
      handleResponseSearchChange('');
    }

    logger.info('results_filters_cleared', correlationId, {
      op: 'results_filters_cleared',
      component: 'ResultsNewRoot',
      correlationId,
      clearedComparisonSearch: Boolean(cmpQuery),
      clearedResponseSearch: Boolean(respQuery),
      duration_ms: performance.now() - startTime
    });
  };

  // Task 4: Add to Compare Deck handler (max 2, de-dupe, order-stable)
  const handleAddToCompareDeck = useCallback((responseId: string) => {
    setCompareDeck(prev => {
      // De-dupe: already in deck
      if (prev.includes(responseId)) {
        logger.info('sxs_deck_add_duplicate', correlationId, {
          op: 'sxs_deck_add',
          component: 'ResultsNewRoot',
          correlationId,
          responseId,
          deckSizeBefore: prev.length,
          success: false,
          err_code: 'DUPLICATE'
        });
        return prev;
      }

      // Max 2: deck full
      if (prev.length >= 2) {
        logger.info('sxs_deck_add_full', correlationId, {
          op: 'sxs_deck_add',
          component: 'ResultsNewRoot',
          correlationId,
          responseId,
          deckSizeBefore: prev.length,
          success: false,
          err_code: 'DECK_FULL'
        });
        return prev;
      }

      // Success: add to end (order-stable)
      const newDeck = [...prev, responseId];

      logger.info('sxs_deck_add_success', correlationId, {
        op: 'sxs_deck_add',
        component: 'ResultsNewRoot',
        correlationId,
        responseId,
        deckSizeBefore: prev.length,
        deckSizeAfter: newDeck.length,
        success: true
      });

      return newDeck;
    });
  }, [correlationId]);

  // Task 4: Remove from Compare Deck handler
  const handleRemoveFromCompareDeck = useCallback((responseId: string) => {
    setCompareDeck(prev => {
      if (!prev.includes(responseId)) {
        logger.info('sxs_deck_remove_not_found', correlationId, {
          op: 'sxs_deck_remove',
          component: 'ResultsNewRoot',
          correlationId,
          responseId,
          deckSizeBefore: prev.length,
          success: false,
          err_code: 'NOT_IN_DECK'
        });
        return prev;
      }

      const newDeck = prev.filter(id => id !== responseId);

      logger.info('sxs_deck_remove_success', correlationId, {
        op: 'sxs_deck_remove',
        component: 'ResultsNewRoot',
        correlationId,
        responseId,
        deckSizeBefore: prev.length,
        deckSizeAfter: newDeck.length,
        success: true
      });

      return newDeck;
    });
  }, [correlationId]);

  // Task 5: Open Side-by-Side view
  const handleOpenSxS = useCallback(() => {
    if (compareDeck.length !== 2) {
      logger.warn('sxs_open_invalid_deck_size', correlationId, {
        op: 'sxs_open',
        component: 'ResultsNewRoot',
        correlationId,
        deckSize: compareDeck.length,
        success: false,
        err_code: 'INVALID_DECK_SIZE'
      });
      return;
    }

    setIsSxSMode(true);

    logger.info('sxs_mode_opened', correlationId, {
      op: 'sxs_mode_toggle',
      component: 'ResultsNewRoot',
      correlationId,
      newMode: 'sxs',
      response1Id: compareDeck[0],
      response2Id: compareDeck[1],
      success: true
    });
  }, [compareDeck, correlationId]);

  // Task 7: Exit Side-by-Side view and clear deck
  const handleExitSxS = useCallback(() => {
    setIsSxSMode(false);
    setCompareDeck([]); // Clear the deck when exiting SxS view

    logger.info('sxs_mode_exited', correlationId, {
      op: 'sxs_mode_toggle',
      component: 'ResultsNewRoot',
      correlationId,
      newMode: 'normal',
      reason: 'user_clicked_exit',
      success: true
    });
  }, [correlationId]);

  // Build modelDetails from responses for consistent display names
  const modelDetails = useMemo(() => {
    const detailsMap = new Map<string, { id: string; displayName: string; providerName: string }>();

    responses.forEach(response => {
      if (!detailsMap.has(response.modelId) && response.model) {
        detailsMap.set(response.modelId, {
          id: response.modelId,
          displayName: response.model.displayName || response.modelId,
          providerName: response.provider?.name || response.providerName || 'Unknown'
        });
      }
    });

    return Array.from(detailsMap.values());
  }, [responses]);

  const feedbackModelSettings = useMemo(() => {
    if (!selectedComparison) return undefined;

    return {
      comparisonTitle: selectedComparison.title,
      prompt: selectedComparison.prompt,
      modelCount: responses.length,
      models: responses.map(r => ({
        modelId: r.modelId,
        provider: r.providerName,
        displayName: r.model?.displayName,
      })),
    } as Record<string, unknown>;
  }, [responses, selectedComparison]);

  useEffect(() => {
    setFeedbackContext({
      comparisonId: selectedComparison?.id ?? null,
      modelSettings: feedbackModelSettings,
      correlationId,
    });
  }, [correlationId, feedbackModelSettings, selectedComparison?.id, setFeedbackContext]);

  // Filter data by search query (NO model filtering)
  const filteredData = useFilteredData(
    comparisons.length > 0 ? comparisons : undefined,
    responses,
    [], // EMPTY ARRAY = show all models (no filtering)
    cmpQuery,
    favorites,
    0, // No model filters available
    respQuery
  );

  // Apply sorting to filtered comparisons
  const sortedComparisons = useMemo(() => {
    // Enrich comparisons with best composite scores before sorting
    // This ensures the "Best Winner Score" sort uses the actual displayed scores
    const enrichedComparisons = filteredData.comparisons.map(comp => {
      const compositeScoresForComparison = allCompositeScoresMap[comp.id];
      const maxScore = (compositeScoresForComparison && compositeScoresForComparison.length > 0)
        ? Math.max(...compositeScoresForComparison.map(s => s.compositeScore ?? 0))
        : null;

      return {
        ...comp,
        // Use max composite score if available, otherwise fall back to comparison's persisted score
        score: maxScore && maxScore > 0 ? maxScore : (comp.score ?? null)
      };
    });

    return sortComparisons(enrichedComparisons, sortOption, favorites);
  }, [filteredData.comparisons, allCompositeScoresMap, sortOption, favorites]);

  useEffect(() => {
    if (deleteFeedback?.type !== 'success') {
      return;
    }

    const timeout = window.setTimeout(() => {
      setDeleteFeedback(current => (current?.type === 'success' ? null : current));
    }, 4000);

    return () => window.clearTimeout(timeout);
  }, [deleteFeedback]);

  // Log component render for diagnostics with correlationId
  useEffect(() => {
    const startTime = performance.now();

    logger.info('results_demo_render', correlationId, {
      op: 'results_demo_render',
      component: 'ResultsNewRoot',
      comparisonId,
      viewMode,
      correlationId,
      filteredResponses: filteredData.filteredResponses,
      totalResponses: filteredData.totalResponses,
      filteredComparisons: filteredData.filteredComparisons,
      totalComparisons: filteredData.totalComparisons,
      success: true,
      duration_ms: performance.now() - startTime
    });
  }, [comparisonId, viewMode, filteredData, correlationId]);

  // SxS Feature Flag Check - Log only when in CDV (Comparison Detail View)
  useEffect(() => {
    if (!selectedComparison) {
      return; // Not in CDV
    }

    logger.info('sxs_feature_flag_check', correlationId, {
      op: 'sxs_feature_flag_check',
      component: 'ResultsNewRoot',
      correlationId,
      flagEnabled: isSxSEnabled,
      viewMode,
      comparisonId: selectedComparison.id,
      compareDeckSize: compareDeck.length,
      success: true
    });

    if (import.meta.env.DEV) {
      console.log('[SxS CDV] Feature flag:', isSxSEnabled, '| Comparison:', selectedComparison.id, '| Deck:', compareDeck);
    }
  }, [isSxSEnabled, selectedComparison, correlationId, viewMode, compareDeck]);

  const deleteErrorMessage = deleteFeedback?.type === 'error' ? deleteFeedback.message : null;
  const deleteSuccessMessage = deleteFeedback?.type === 'success' ? deleteFeedback.message : null;

  useEffect(() => {
    if (!deleteFeedback) {
      lastDeleteToastRef.current = null;
      return;
    }

    const toastKey = `${deleteFeedback.type}:${deleteFeedback.message}`;
    if (lastDeleteToastRef.current === toastKey) {
      return;
    }

    lastDeleteToastRef.current = toastKey;

    if (deleteFeedback.type === 'success') {
      showDestructiveToast('Comparison deleted', 4500);
    } else {
      showErrorToast('Unable to delete comparison');
    }
  }, [deleteFeedback, showDestructiveToast, showErrorToast]);

  return (
    <>
      <ToastContainer
        toasts={toasts}
        onDismiss={removeToast}
        position="top-right"
      />
      <div
        data-testid="results-new-root"
        className="results-grid w-full h-screen flex flex-col md:flex-row"
        style={{
          height: 'calc(100vh - var(--main-nav-height, 79px))' // Dynamic header height via CSS variable
        }}
      >
      {/* Sidebar with view mode pills - Desktop only - Hidden on mobile since tabs are in main header */}
      {comparisons.length > 0 && !isMobile && (
        <ResultsSidebar
          className={`w-full ${sidebarCollapsed ? 'md:w-12' : 'md:w-64'} flex-shrink-0 border-b md:border-b-0 md:border-r border-slate-700/50 transition-all duration-300 ${sidebarCollapsed ? 'md:overflow-hidden' : ''}`}
        density={density}
        onDensityChange={setDensity}
        cmpQuery={cmpQuery}
        onCmpQueryChange={handleComparisonSearchChange}
        respQuery={respQuery}
        onRespQueryChange={handleResponseSearchChange}
        isComparisonDetailOpen={!!displayComparison}
        comparisonDetailView={detailView}
        onComparisonDetailViewChange={setDetailView}
        consensusTab={consensusTab}
        onConsensusTabChange={setConsensusTab}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        hideCompletely={isResponseDetailOpen}
        compareDeckResponses={responses.filter(r => compareDeck.includes(r.id))}
        onCompareDeckRemove={(responseId) => {
          setCompareDeck(prev => prev.filter(id => id !== responseId));
        }}
        onOpenSxS={isSxSEnabled ? handleOpenSxS : undefined}
        isSxSMode={isSxSMode}
        onExitSxS={isSxSEnabled ? handleExitSxS : undefined}
        correlationId={correlationId}
        modelFilterResponses={responses}
        modelFilterModelDetails={modelDetails}
        modelFilterSelectedModels={selectedModels}
        modelFilterMode={filterMode}
        onModelFilterChange={handleFilterChange}
        hasComparisons={comparisons.length > 0}
        feedbackComparisonId={selectedComparison?.id || null}
        feedbackModelSettings={feedbackModelSettings}
        isAnonymous={isAnonymous}
        />
      )}

      {/* Main content area */}
      <main className="flex-1 overflow-y-auto bg-slate-900/50" data-testid="results-pane">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pt-4 sm:pt-6 pb-2 sm:pb-6 md:pb-8">
          {deleteFeedback && !displayComparison && (
            <div
              role={deleteFeedback.type === 'error' ? 'alert' : 'status'}
              className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
                deleteFeedback.type === 'error'
                  ? 'border-rose-500/50 bg-slate-900/70 text-rose-100 shadow-lg shadow-rose-900/30'
                  : 'border-sky-500/40 bg-slate-900/70 text-slate-100 shadow-lg shadow-slate-900/30'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <span>{deleteFeedback.message}</span>
                <button
                  onClick={dismissDeleteFeedback}
                  className="text-xs text-slate-300 hover:text-white opacity-80 hover:opacity-100"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Anonymous session warning banner - only show if ANONYMOUS flag is enabled */}
          {isAnonymous && anonymousFlagEnabled && (
            <AnonymousBanner
              className="mb-6"
            />
          )}

          {/* STEP 6: Loading state */}
          {comparisonsLoading && (
            <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-slate-700/50 rounded w-3/4"></div>
                <div className="h-4 bg-slate-700/50 rounded w-1/2"></div>
                <div className="h-4 bg-slate-700/50 rounded w-5/6"></div>
              </div>
              <p className="text-slate-400 text-sm mt-4 text-center">Loading comparisons...</p>
            </div>
          )}

          {/* Error state for API failures */}
          {comparisonsError && !comparisonsLoading && (
            <div className="bg-red-900/20 backdrop-blur-sm border border-red-700/50 rounded-2xl p-8 mb-6">
              <div className="w-16 h-16 bg-red-700/50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2 text-center">
                Failed to load comparisons
              </h2>
              <p className="text-red-300 text-sm text-center mb-4">
                {comparisonsError}
              </p>
              <div className="flex justify-center">
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-red-700/30 hover:bg-red-700/50 border border-red-600/50 rounded-lg text-red-200 text-sm transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* REMOVED: Empty state for no models - model filtering disabled */}

          {/* Comparisons View */}
          {!comparisonsLoading && (
            <>
              <div hidden={!isComparisonsView || !!displayComparison}>
                {comparisons.length > 0 && (
                  <div className="mb-6 flex items-start justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-white">Comparisons</h2>
                      <p className="text-slate-400 text-sm mt-1">
                        {filteredData.filteredComparisons} of {filteredData.totalComparisons} comparisons
                      </p>
                    </div>
                    <ComparisonSortControl
                      value={sortOption}
                      onChange={handleSortChange}
                      hideScoreOption={false}
                      isAnonymous={isAnonymous}
                    />
                  </div>
                )}
                <ComparisonsList
                  comparisons={sortedComparisons}
                  responses={filteredData.responses}
                  scoringResults={scoringResults}
                  modelDetails={modelDetails}
                  density={density}
                  onComparisonClick={handleComparisonClick}
                  onClearFilters={() => {}} // REMOVED: No filters to clear
                  favorites={favorites}
                  onFavoriteToggle={handleFavoriteToggle}
                  selectedModels={selectedModels}
                  filterMode={filterMode}
                  compositeScoresMap={allCompositeScoresMap}
                  hasMore={hasMoreComparisons}
                  loadingMore={comparisonsLoadingMore}
                  onLoadMore={loadMoreComparisons}
                  isAnonymous={isAnonymous}
                />
              </div>

              {displayComparison && (
                <div hidden={!isComparisonsView}>
                  <ComparisonDetail
                    comparison={displayComparison}
                    responses={filteredData.responses.filter(r => r.comparisonId === displayComparison.id)}
                    scoringResults={scoringResults}
                    compositeScores={compositeScores || undefined}
                    scoresEnabled={scoresFeatureEnabled}
                    onBack={handleBackToList}
                    onNavigateToParent={handleNavigateToParent}
                    onNavigateToAll={handleNavigateToAll}
                    onOpenResponse={handleOpenResponseDetail}
                    density={density}
                    onFavoriteToggle={handleFavoriteToggle}
                    isFavorited={favorites.has(displayComparison.id)}
                    detailView={detailView}
                    onDetailViewChange={setDetailView}
                    consensusTab={consensusTab}
                    onConsensusTabChange={setConsensusTab}
                    winnerAiId={winnerAiId}
                    winnerScore={winnerScore}
                    responsesLoading={responsesLoading}
                    responsesRefreshing={responsesRefreshing}
                    responseError={responsesError}
                    onResponseRetry={refetchResponses}
                    onDeleteComparison={handleDeleteComparison}
                    deletingComparisonId={deletingComparisonId}
                    deleteError={deleteErrorMessage}
                    deleteSuccessMessage={deleteSuccessMessage}
                    onDismissDeleteError={dismissDeleteFeedback}
                    onComparisonUpdated={handleComparisonUpdated}
                    onScoresRefetch={refetchScoresAndWait}
                    compareDeck={compareDeck}
                    onAddToCompareDeck={isSxSEnabled ? handleAddToCompareDeck : undefined}
                    onRemoveFromCompareDeck={isSxSEnabled ? handleRemoveFromCompareDeck : undefined}
                    isSxSMode={isSxSMode}
                    sxsSplitPct={sxsSplitPct}
                    onSxsSplitChange={setSxsSplitPct}
                    onExitSxS={isSxSEnabled ? handleExitSxS : undefined}
                    isAnonymous={isAnonymous}
                  />
                </div>
              )}
            </>
          )}

          {/* Responses View */}
          {!comparisonsLoading && !isComparisonsView && (
            <>
              <ResponsesNewShell
                responses={filteredData.responses}
                comparisons={filteredData.comparisons}
                density={density}
                searchQuery={respQuery}
                initialResponseId={initialResponseId}
                compositeScores={compositeScores || undefined}
                scoresEnabled={scoresFeatureEnabled}
                scoringResults={scoringResults}
                onDetailOpenChange={handleResponseDetailOpenChange}
                onDetailVisit={handleResponseDetailVisit}
                isAnonymous={isAnonymous}
                onChatOpen={(response) => {
                  logger.info('chat_button_clicked', correlationId, {
                    op: 'chat_button_clicked',
                    component: 'ResultsNewRoot',
                    responseId: response.id,
                    modelId: response.modelId,
                    correlationId
                  });
                  // TODO Step 6: Open ChatDrawer
                }}
                onOpenParent={handleOpenParent}
                onNavigateToAll={handleNavigateToAllComparisons}
                onClearFilters={handleClearFilters}
              />
            </>
          )}
        </div>
      </main>
    </div>
    </>
  );
};
